import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { SITE_INFO } from '../data/siteData'
import { CorePillars } from '../components/CorePillars'
import { ForloebHighlight } from '../components/ForloebHighlight'
import { TestimonialsSlider } from '../components/TestimonialsSlider'
import { VideoPlayer } from '../components/VideoPlayer'
import { useCMSCollection, useCMSSide, useVirksomhedsoplysninger } from '../hooks/useCMS'

const HERO_SLIDES = [
  {
    image: '/_assets/20241001-091341637-ios.png',
    alt: 'Bo Steendahl kropsterapi behandling',
  },
  {
    image: '/_assets/20241001-091341642-ios.png',
    alt: 'Bo Steendahl behandler klient i klinikken',
  },
  {
    image: '/_assets/sddefault.webp',
    alt: 'Bo Steendahl - Totum Kropsterapeut',
  },
]

export function HomePage() {
  const [activeSlide, setActiveSlide] = useState(0)

  const { data: cmsSlides } = useCMSCollection('hero_slides')
  const { side: forsideData } = useCMSSide('forside')
  const { info } = useVirksomhedsoplysninger()

  const slides = Array.isArray(cmsSlides) && cmsSlides.length > 0
    ? [...cmsSlides]
        .sort((a, b) => (a.sortering || 0) - (b.sortering || 0))
        .map((s) => ({
          image: Array.isArray(s.billede) ? s.billede[0] : s.billede,
          alt: s.titel,
        }))
    : HERO_SLIDES

  const prevSlide = () => {
    setActiveSlide((curr) => (curr - 1 + slides.length) % slides.length)
  }

  const nextSlide = () => {
    setActiveSlide((curr) => (curr + 1) % slides.length)
  }

  useSEO({
    title: forsideData?.seo_titel || 'BoSteendahl.dk — Kropsterapi, Coaching & Hypnoterapi',
    description: forsideData?.seo_beskrivelse || 'Bo Steendahl tilbyder forløb inden for Kropsterapi, Mindset Coaching og Hypnoterapi i Fredericia. Forandring indefra med fokus på helhed og balance.',
    ogImage: forsideData?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/logo-11-dec.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* 1. HERO SECTION - FULL-WIDTH 40/60 SPLIT HERO */}
      <section className="relative w-full min-h-[580px] lg:h-[calc(100vh-80px)] lg:min-h-[620px] lg:max-h-[780px] bg-[#132835] overflow-hidden flex flex-col lg:flex-row items-center border-b border-[#1b3a4b]">
        {/* Subtle low-opacity blueprint / grid line illustration behind left content */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255, 255, 255, 0.2) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255, 255, 255, 0.2) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
          aria-hidden="true"
        />

        {/* Subtle understated glow */}
        <div
          className="absolute top-1/4 left-1/6 w-96 h-96 bg-[#7c875a]/15 rounded-full blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        {/* Right side: large full-bleed hero image covering right ~60% */}
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-[60%] w-full h-[340px] sm:h-[400px] lg:h-full relative overflow-hidden order-1 lg:order-2">
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                idx === activeSlide ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.alt}
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}

          {/* Soft dark overlay and gradient near the split for smooth visual transition - more transparent */}
          <div
            className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#132835]/85 via-[#132835]/30 lg:via-[#132835]/35 to-black/10 z-10 pointer-events-none"
            aria-hidden="true"
          />
        </div>

        {/* Left side content aligned with site's standard page container */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-8 flex items-center order-2 lg:order-1">
          {/* Text block vertically centered, extending slightly across split boundary into image side */}
          <div className="w-full lg:w-[50%] xl:w-[48%] space-y-4 sm:space-y-5 text-left">
            {/* Pill-style eyebrow label */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/90 text-xs font-semibold uppercase tracking-wider backdrop-blur-xs">
              <span className="w-2 h-2 rounded-full bg-[#7c875a]" />
              <span>Bo Steendahl · Forandring indefra</span>
            </div>

            {/* Large bold display heading with tight readable line-height */}
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-[2.35rem] xl:text-[2.75rem] font-bold tracking-tight text-white leading-[1.14]">
              Vi udvikles og forandres som menneske, men den indre kerne består.
            </h1>

            {/* Core Pillars pill */}
            <div>
              <div className="inline-block py-1.5 px-3 rounded-lg bg-white/10 border border-white/15 backdrop-blur-xs">
                <span className="text-xs sm:text-sm font-bold text-[#E5E0D8] tracking-widest uppercase">
                  KROPSTERAPI <span className="text-[#7c875a]">·</span> COACHING <span className="text-[#7c875a]">·</span> HYPNOTERAPI
                </span>
              </div>
            </div>

            {/* Short body paragraph */}
            <p className="text-sm sm:text-base lg:text-[0.95rem] text-white/80 max-w-xl leading-relaxed">
              Med udgangspunkt i den dybe forbindelse mellem krop og psyke anskuer jeg mennesket som en helhed. Jeg hjælper dig med at bygge bro mellem dine følelser, dine tanker og din krop gennem sammenhængende forløb.
            </p>

            {/* Two horizontal CTA buttons */}
            <div className="pt-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2.5 bg-[#7c875a] hover:bg-[#6e784f] text-white px-6 py-3 rounded-full text-xs sm:text-sm font-bold tracking-wide transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              >
                <span>Hør mere om et forløb</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <Link
                to="/forloeb"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 px-5 py-3 rounded-full text-xs sm:text-sm font-bold tracking-wide transition-all hover:-translate-y-0.5 backdrop-blur-xs"
              >
                <span>Sådan arbejder vi</span>
              </Link>
            </div>

            {/* Thin horizontal divider and small trust items with icons */}
            <div className="pt-4 border-t border-white/15">
              <div className="flex flex-wrap items-center gap-y-2 gap-x-5 text-xs text-white/75 font-medium">
                <span className="inline-flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-[#7c875a] text-sm" aria-hidden="true" />
                  <span>RAB-godkendt Kropsterapeut</span>
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-[#7c875a] text-sm" aria-hidden="true" />
                  <span>Kun forløb — ingen enkeltsessioner</span>
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-[#7c875a] text-sm" aria-hidden="true" />
                  <span>Centralt i Fredericia</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Simple slider navigation control fixed to bottom edge on the split between columns (Desktop only) */}
        <div className="hidden lg:flex items-center absolute bottom-0 left-[40%] z-20 bg-[#0e1d27] border-t border-r border-white/15">
          <button
            type="button"
            onClick={prevSlide}
            aria-label="Forrige billede"
            className="w-12 h-12 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors border-r border-white/15 cursor-pointer"
          >
            <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={nextSlide}
            aria-label="Næste billede"
            className="w-12 h-12 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* 2. THE THREE CORE PILLARS SECTION */}
      <CorePillars />

      {/* 3. FORLØB — IKKE ENKELTBEHANDLINGER HIGHLIGHT */}
      <ForloebHighlight />

      {/* 4. OM BO STEENDAHL — KORT INTRODUKTION */}
      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image / Atmosphere (Span 5) */}
            <div className="lg:col-span-5 relative order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border border-[#E5E0D8]">
                <img
                  src="/_assets/20220903-135741.jpg"
                  alt="Bo Steendahl i samtale og kropsterapi"
                  className="w-full h-[450px] sm:h-[550px] lg:h-[600px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 hidden sm:block bg-[#1b3a4b] text-white p-5 rounded-2xl shadow-xl max-w-xs border border-white/10">
                <p className="font-serif text-sm italic leading-relaxed text-[#FAF8F5]">
                  "Det er mit ønske og ambition at skabe et trygt, nærværende og omsorgsfuldt rum..."
                </p>
                <span className="text-xs text-[#7c875a] font-semibold mt-2 block">— Bo Steendahl</span>
              </div>
            </div>

            {/* Story prose (Span 7) */}
            <div className="lg:col-span-7 space-y-6 order-1 lg:order-2">
              <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
                Mød Bo Steendahl
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1b3a4b] leading-tight">
                Et livssyn formet af håndværk, empati og solid livserfaring
              </h2>
              <div className="space-y-4 text-base text-[#475569] leading-relaxed">
                <p>
                  Jeg har siden mine helt unge år været interesseret i livsopfattelsen og forståelsen af kroppen. Gennem årene har jeg fordybet mig i kinesiologi, akupressur, zoneterapi samt anatomi og fysiologi.
                </p>
                <p>
                  I sommeren 2018 tog jeg beslutningen: Jeg uddannede mig til Totum Kropsterapeut. Det var det håndværk, jeg altid havde søgt, for at kunne støtte, udfordre, inspirere og hjælpe de mennesker, jeg møder på min vej.
                </p>
                <p>
                  Kombineret med mange års erfaring med NLP og mental coaching ved jeg, at et forløb skaber de bedste forudsætninger for at give slip på gamle traumer og lade energien og håbet vende tilbage.
                </p>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <Link
                  to="/om-bo"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1b3a4b] hover:text-[#7c875a] transition-colors"
                >
                  <span>Læs hele historien om Bo</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <span className="hidden sm:inline text-[#CBD5E1]">·</span>
                <Link
                  to="/mine-9-grundprincipper"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1b3a4b] hover:text-[#7c875a] transition-colors"
                >
                  <span>Se mine 9 grundprincipper</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. UDVALGT EKSISTERENDE HOVEDBUDSKAB (VERBATIM SCRAPE PROSE) */}
      <section className="py-20 bg-[#F4EFEB] border-y border-[#E5E0D8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block mb-1">
              Forandring indefra
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1b3a4b]">
              Kontakten mellem hoved og krop er afgørende
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E0D8] space-y-4">
              <div className="w-10 h-10 rounded-xl bg-[#1b3a4b]/10 text-[#1b3a4b] flex items-center justify-center text-lg">
                <i className="fa-solid fa-heart-pulse" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Krop, sind og tanker påvirker hinanden
              </h3>
              <p className="text-sm text-[#475569] leading-relaxed">
                Store følelsesmæssige påvirkninger som nervøsitet, vrede, angst, skam, sorg og stress kan sætte sig som uro, spændinger eller smerte i kroppen. Årsagen, som ligger bagved, kan være svær at finde eller sætte ord på.
              </p>
              <p className="text-sm text-[#64748B] leading-relaxed">
                TOTUM Kropsterapi hjælper dig med at skabe bedre kontakt mellem dine tanker, dit sind og din krop, så du kan genfinde roen.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E0D8] space-y-4">
              <div className="w-10 h-10 rounded-xl bg-[#7c875a]/20 text-[#1b3a4b] flex items-center justify-center text-lg">
                <i className="fa-solid fa-sparkles" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Tænd håbets lys og giv dig selv lov til at drømme
              </h3>
              <p className="text-sm text-[#475569] leading-relaxed">
                Forestil dig et liv, hvor du ikke er begrænset og styret af smerter, sorg, stress, vrede, depression eller angst. Luk øjnene og mærk befrielsen, når du bryder ud af det mønster, som holder dig fast.
              </p>
              <p className="text-sm text-[#64748B] leading-relaxed">
                Enhver rejse starter med det første skridt. Jeg vil gerne hjælpe dig med at komme afsted og finde din vej.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. VIDEO STORYTELLING ELEMENT */}
      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block mb-1">
              Profilfilm
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1b3a4b]">
              Bo Steendahl — Forandring indefra
            </h2>
            <p className="mt-2 text-sm text-[#64748B]">
              Se filmen og hør mere om filosofien, rammerne i Fredericia og tilgangen til forløb.
            </p>
          </div>

          <VideoPlayer
            type="youtube"
            source="29PQYHSfCTA"
            poster="https://i.ytimg.com/vi/29PQYHSfCTA/hqdefault.jpg"
            title="BoSteendahlDK profilfilm 24"
            description="Bo Steendahl - Forandring indefra"
          />

          {/* Audio/Teaser note on Claus */}
          <div className="mt-8 p-6 rounded-2xl bg-[#F4EFEB] border border-[#E5E0D8] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#1b3a4b] text-white flex items-center justify-center text-lg flex-shrink-0">
                <i className="fa-solid fa-volume-high" aria-hidden="true" />
              </div>
              <div>
                <h4 className="font-serif text-base font-bold text-[#1b3a4b]">
                  Hør hele Claus' fortælling
                </h4>
                <p className="text-xs sm:text-sm text-[#64748B]">
                  Om hans vilde forvandling fra stress, hårtab og indre kaos – til dyb ro, styrke og selvkontrol.
                </p>
              </div>
            </div>
            <Link
              to="/video"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1b3a4b] hover:text-[#7c875a] whitespace-nowrap"
            >
              <span>Se alle videoer</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. ANMELDELSER (INFINITE SLIDER) */}
      <TestimonialsSlider />
    </div>
  )
}
